import AOS from 'aos';

let mixitup = require('mixitup');

/**
 * Initializes the SelectA1 module, enabling filtering and visibility toggling
 * for items within a given block using the MixItUp library.
 *
 * Also handles custom accessible dropdown behavior.
 */
function SelectA1() {
    /**
     * Initializes filtering and dropdown accessibility logic within a block.
     *
     * @param {HTMLElement} block - The block element containing the filtering module.
     */
    function init(block) {
        const selectWrapper = block.querySelector(
            '.o-select-a-1__select-wrapper',
        );
        const selectField = block.querySelector('.js-select-field');
        const selectButton = block.querySelector('.o-select-a-1__button');
        const dropdown = block.querySelector('.o-select-a-1__dropdown');
        const options = dropdown.querySelectorAll(
            '.o-select-a-1__dropdown-list-item',
        );
        const selectedValue = selectButton.querySelector(
            '.o-select-a-1__selected-value',
        );

        // Bail out early if required elements are not found
        if (!selectField || !selectButton || !dropdown) {
            return;
        }

        // Initialize MixItUp instance for filtering.
        const miu = mixitup(block, {
            animation: {
                duration: 400,
                effectsIn: 'fade',
                effectsOut: 'fade',
                easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
            },
            load: {
                filter: 'none', // for redeploying
            },
        });

        // Filtering on change (if fallback <select> added in the future)
        selectField.addEventListener('change', () => {
            const filterValue =
                selectField.value === 'all'
                    ? 'all'
                    : `[data-value="${selectField.value}"]`;
            miu.filter(filterValue);
        });

        // Dropdown behavior
        let focusedIndex = -1;

        const toggleDropdown = (expand = null) => {
            const isCurrentlyHidden =
                dropdown.classList.contains('invisible') &&
                dropdown.classList.contains('opacity-0');

            const isOpen = expand !== null ? expand : isCurrentlyHidden;

            dropdown.classList.toggle('invisible', !isOpen);
            dropdown.classList.toggle('opacity-0', !isOpen);
            selectButton.setAttribute('aria-expanded', isOpen);

            if (isOpen) {
                focusedIndex = [...options].findIndex((option) =>
                    option.classList.contains('selected'),
                );
                focusedIndex = focusedIndex === -1 ? 0 : focusedIndex;
                updateFocus();
            } else {
                focusedIndex = -1;
                selectButton.focus();
            }
        };

        const closeDropdown = () => {
            const isCurrentlyHidden =
                dropdown.classList.contains('invisible') &&
                dropdown.classList.contains('opacity-0');

            if (isCurrentlyHidden) {
                return;
            }

            dropdown.classList.add('invisible');
            dropdown.classList.add('opacity-0');
            selectButton.setAttribute('aria-expanded', 'false');
            focusedIndex = -1;
            selectButton.focus();
        };

        const updateFocus = () => {
            options.forEach((option, index) => {
                option.setAttribute(
                    'tabindex',
                    index === focusedIndex ? '0' : '-1',
                );
                if (index === focusedIndex) {
                    option.focus();
                }
            });
        };

        const handleOptionSelect = (option) => {
            options.forEach((opt) => opt.classList.remove('selected'));
            option.classList.add('selected');
            selectedValue.textContent = option.textContent.trim();

            const selected = option.getAttribute('value');
            const filterValue =
                selected === 'all' ? 'all' : `[data-value="${selected}"]`;

            miu.filter(filterValue).then(() => {
                AOS.refresh(); // Reinitialize AOS animations after filtering
            });
        };

        options.forEach((option) => {
            option.addEventListener('click', () => {
                handleOptionSelect(option);
                toggleDropdown(false);
            });
        });

        selectButton.addEventListener('click', () => {
            toggleDropdown();
        });

        selectButton.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                toggleDropdown(true);
            } else if (event.key === 'Escape') {
                toggleDropdown(false);
            }
        });

        dropdown.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                focusedIndex = (focusedIndex + 1) % options.length;
                updateFocus();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                focusedIndex =
                    (focusedIndex - 1 + options.length) % options.length;
                updateFocus();
            } else if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOptionSelect(options[focusedIndex]);
                toggleDropdown(false);
            } else if (event.key === 'Escape') {
                toggleDropdown(false);
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (event) => {
            const isOutsideClick = !selectWrapper.contains(event.target);
            if (isOutsideClick) {
                closeDropdown();
            }
        });
    }

    // Initialize all instances
    document
        .querySelectorAll('[data-module="selectA1"]')
        .forEach((block) => init(block));
}

export default SelectA1;
